import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Download, Upload, Trash2, X, Search, History, Filter, ScrollText, AlertTriangle } from "lucide-react";
import { PageHeader, PageBody, Field, StatusPill, statusTone } from "@/components/PageChrome";
import { Input, Select, Button } from "@/components/controls";
import ImageInput from "@/components/ImageInput";
import { T } from "@/lib/testIds";
import { usePerms } from "@/lib/auth";
import SkuTimelineTrends from "@/components/SkuTimelineTrends";
import PaginationBar from "@/components/PaginationBar";
import useClientPagination from "@/hooks/useClientPagination";
import FieldFilterBar, { applyFieldFilters } from "@/components/FieldFilterBar";
import FilterPresets from "@/components/FilterPresets";
import InlineCell from "@/components/InlineCell";
import ImageThumb from "@/components/ImageThumb";

const TABS = [
  { key: "stock", label: "Stock" },
  { key: "in_transit", label: "In-Transit" },
  { key: "placed", label: "Placed" },
];

// Server-computed, display-only fields added by _apply_metrics /
// _aggregate_sku_metrics (backend/routes_inventory.py) — derived from orders,
// never editable here. They ride along on every table row for display, but
// must never be sent back in a PATCH/POST body: doing so persists them as if
// the user had edited them, which shows up as garbled diffs in the Change
// Log for fields nobody touched.
const COMPUTED_INVENTORY_FIELDS = ["qty_placed", "in_transit_allocated", "shipment_ids"];
function stripComputedFields(obj) {
  const clean = { ...obj };
  COMPUTED_INVENTORY_FIELDS.forEach((k) => delete clean[k]);
  return clean;
}

// Format a UTC ISO timestamp as "YYYY-MM-DD HH:MM:SS IST" — IST is UTC+05:30
// and every server-side timestamp lands as UTC ISO string. Consolidated here
// so audit-log columns render in the ops team's local wall time (Feb 21 2026).
function fmtIst(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    // Shift by IST offset (+5h30m). getTime()+offsetMs, then read UTC pieces.
    const shifted = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
    const y = shifted.getUTCFullYear();
    const m = String(shifted.getUTCMonth() + 1).padStart(2, "0");
    const day = String(shifted.getUTCDate()).padStart(2, "0");
    const hh = String(shifted.getUTCHours()).padStart(2, "0");
    const mm = String(shifted.getUTCMinutes()).padStart(2, "0");
    const ss = String(shifted.getUTCSeconds()).padStart(2, "0");
    return `${y}-${m}-${day} ${hh}:${mm}:${ss} IST`;
  } catch { return String(iso); }
}

// Filter registry — uses selects for reference-table + dropdown fields,
// number ranges for quantities, and text for free-form ids. Empty/blank
// matching is available on every chip via the shared FieldFilterBar ∅ toggle.
// The exact 24 fields per user spec (Feb 2026). Keep this list in sync with
// MODULE_FIELDS['inventory'] in backend/routes_misc.py + the table header.
const INVENTORY_FILTER_FIELDS = [
  // 1-4: Identity
  // Feb 27 2026 — System SKU + Product Name are finite per-inventory sets;
  // upgrade both to multi-select with search + `(blank / empty)` toggle.
  // Options are derived cascadingly from the currently-filtered subset,
  // see `filterDropdowns` below.
  { key: "system_sku", label: "System SKU", type: "select", opts: (d) => d.system_sku || [] },
  { key: "product_type", label: "Product Type", type: "select", opts: (d) => d.product_type || [] },
  { key: "product_name", label: "Product Name", type: "select", opts: (d) => d.product_name || [] },
  // Feb 27 2026 — variants are a finite per-SKU vocabulary, so upgrade to
  // multi-select with search. Options are derived from currently loaded rows.
  { key: "product_variant", label: "Variant", type: "select", opts: (d) => d.product_variant || [] },
  // 5-8: Quantities
  { key: "qty", label: "Qty in Stock", type: "number-range" },
  { key: "in_transit_qty", label: "In-Transit (Unalloc.)", type: "number-range" },
  { key: "qty_placed", label: "Qty Placed", type: "number-range" },
  { key: "in_transit_allocated", label: "In-Transit (Alloc.)", type: "number-range" },
  // 9-11: Location + tags
  // Feb 27 2026 — Bin ID + Tags upgraded to multi-select with search +
  // blank/empty toggle. Options derived from rows in `filterDropdowns`.
  { key: "bin_id", label: "Bin ID", type: "select", opts: (d) => d.bin_id || [] },
  { key: "tags", label: "Tags", type: "select", opts: (d) => d.tags || [] },
  { key: "warehouse_name", label: "Warehouse", type: "select", opts: (d) => d.warehouse_name || [] },
  // 12-13: Assets
  { key: "image_url", label: "Image URL", type: "text" },
  // Shipment IDs are a finite enumeration too.
  { key: "shipment_ids", label: "Shipment IDs", type: "select", opts: (d) => d.shipment_ids || [] },
  // 14-17: Physical dimensions
  { key: "length_cm", label: "Length (cm)", type: "number-range" },
  { key: "width_cm", label: "Width (cm)", type: "number-range" },
  { key: "height_cm", label: "Height (cm)", type: "number-range" },
  { key: "weight_gram", label: "Weight (g)", type: "number-range" },
  // URL fields (Feb 2026 spec — filterable across all modules)
  { key: "source_url", label: "Source URL", type: "text" },
  { key: "china_price", label: "China Price ¥", type: "money-range" },
  { key: "china_shipping", label: "China Shipping ¥", type: "money-range" },
  { key: "china_weight_gram", label: "China Weight (g)", type: "number-range" },
  { key: "moq", label: "MOQ", type: "number-range" },
  // 23-24: Timestamps (auto-managed)
  { key: "created_at", label: "Created At", type: "date-range" },
  { key: "updated_at", label: "Updated At", type: "date-range" },
];

export default function Inventory() {
  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [dropdowns, setDropdowns] = useState({});
  const [q, setQ] = useState("");
  const [advFilters, setAdvFilters] = useState({});
  const [accountFilter, setAccountFilter] = useState("");
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [tab, setTab] = useState("stock");
  const [inTransitTab, setInTransitTab] = useState("allocated");
  const [editing, setEditing] = useState(null);
  const [reviewCount, setReviewCount] = useState(0);
  useEffect(() => {
    api.get("/inventory/review").then(({ data }) => setReviewCount(data.count || 0)).catch(() => {});
  }, []);
  // When user changes qty on the edit drawer, we hold the pending payload
  // here and open the AdjustQtyModal — the raw save() is bypassed for qty
  // changes so every stock movement carries a proper reason/condition/source
  // audit entry (Feb 21 2026 spec).
  const [adjustPrompt, setAdjustPrompt] = useState(null); // {row, delta}
  const [historyRecord, setHistoryRecord] = useState(null);
  // Per-row change-log viewer — populated when user clicks the scroll icon.
  // `{record, logs: [...], loading}` shape.
  const [logsRecord, setLogsRecord] = useState(null);
  const [revealedCols, setRevealedCols] = useState(() => {
    try { return JSON.parse(localStorage.getItem("inventory_reveal_cols") || "[]"); }
    catch { return []; }
  });
  const toggleRevealCol = (key) => {
    setRevealedCols((cur) => {
      const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
      localStorage.setItem("inventory_reveal_cols", JSON.stringify(next));
      return next;
    });
  };
  const { isAdmin, canView, canEdit, canDelete } = usePerms();
  const editable = canEdit("inventory");
  const deletable = canDelete("inventory");
  // Feb 28 2026 — row-selection state for bulk delete.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Mar 2 2026 — Shipped-qty overlay for the Inventory grid.
  //   { "<system_sku>||<warehouse_name>": { procurement_id, shipped_qty, count } }
  // Populated in bulk after `rows` loads (single POST /procurement/shipped-qty-map)
  // so we render an inline-editable Shipped Qty column without an N+1 stampede.
  const [shippedQtyMap, setShippedQtyMap] = useState({});
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const wipeAll = async () => {
    const phrase = "WIPE ALL INVENTORY";
    const entry = window.prompt(
      `This will permanently delete EVERY inventory row.\nType "${phrase}" to confirm.`,
    );
    if (entry !== phrase) {
      if (entry !== null) toast.error("Confirmation phrase did not match — wipe cancelled.");
      return;
    }
    try {
      const { data } = await api.post("/inventory/admin/wipe", { confirm: phrase });
      toast.success(`Wiped ${data.deleted} inventory rows.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const load = async (searchQuery) => {
    try {
      // Pass the current input value explicitly so pressing Enter after typing
      // fires the search with the fresh value (no stale-closure risk).
      const qParam = searchQuery !== undefined ? searchQuery : q;
      const [inv, wh, acc, dd] = await Promise.all([
        // Ask for the full inventory (up to 50k rows) so client-side
        // pagination + filtering see every SKU. Backend caps at 100k.
        api.get("/inventory", { params: { limit: 50000, ...(qParam ? { q: qParam } : {}) } }),
        api.get("/warehouse"),
        api.get("/accounts"),
        api.get("/settings/dropdowns").catch(() => ({ data: {} })),
      ]);
      setRows(inv.data);
      setWarehouses(wh.data);
      setAccounts(acc.data || []);
      setDropdowns(dd.data || {});
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Mar 2 2026 — pull the shipped_qty map once every time the inventory
  // dataset changes. Batched into a single POST so 1k+ rows resolve in one
  // round-trip. Missing (system_sku, warehouse_name) pairs simply don't show
  // up in the response map — the column then renders a "—" for that row.
  useEffect(() => {
    if (!rows.length) { setShippedQtyMap({}); return; }
    const pairs = rows
      .filter((r) => r.system_sku && r.warehouse_name)
      .map((r) => ({ system_sku: r.system_sku, warehouse_name: r.warehouse_name }));
    if (!pairs.length) { setShippedQtyMap({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/procurement/shipped-qty-map", { pairs });
        if (!cancelled) setShippedQtyMap(data.map || {});
      } catch { /* silent — the column just falls back to "—" */ }
    })();
    return () => { cancelled = true; };
  }, [rows]);

  const saveShippedQty = async (row, next) => {
    const entry = shippedQtyMap[`${row.system_sku}||${row.warehouse_name}`];
    if (!entry?.procurement_id) {
      toast.error("No linked procurement record for this row");
      return;
    }
    try {
      const payload = { shipped_qty: next === "" ? null : Number(next) };
      await api.patch(`/procurement/${entry.procurement_id}`, payload);
      setShippedQtyMap((prev) => ({
        ...prev,
        [`${row.system_sku}||${row.warehouse_name}`]: {
          ...prev[`${row.system_sku}||${row.warehouse_name}`],
          shipped_qty: payload.shipped_qty,
        },
      }));
      toast.success("Shipped qty updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Feb 27 2026 — cascading multi-select options.
  //
  // Each finite-set filter (bin_id, tags, product_variant, shipment_ids,
  // warehouse_name) computes its dropdown options from the *result set*
  // that would exist if every OTHER filter were applied — but not itself.
  // This mirrors the way Google Sheets / Airtable cascade filters and
  // matches ops' mental model: "if I already picked Warehouse=NOIDA and
  // Bin=S138, the Variant dropdown should only list variants that live
  // inside those 22 rows."
  //
  // Excluding the field's own selection from its own option list keeps
  // the popover from collapsing to just the currently-picked values once
  // the user starts checking boxes (so they can still deselect / pick
  // more without opening every other filter again).
  const filterDropdowns = useMemo(() => {
    const collect = (subset, getter) => {
      const set = new Set();
      for (const r of subset) {
        const v = getter(r);
        if (Array.isArray(v)) {
          for (const x of v) if (x !== null && x !== undefined && x !== "") set.add(String(x));
        } else if (v !== null && v !== undefined && v !== "") {
          set.add(String(v));
        }
      }
      return [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    };
    // For a given field `key`, return rows that pass every OTHER active
    // filter — that's the pool we harvest option values from.
    const scoped = (key) => {
      const others = { ...advFilters };
      delete others[key];
      return rows.filter((r) => applyFieldFilters(r, others, INVENTORY_FILTER_FIELDS));
    };
    const wareOpts = warehouses.map((w) => w.name).filter(Boolean);
    return {
      ...dropdowns,
      warehouse_name: (() => {
        // Union of warehouse master + inventory-derived values, then narrow
        // to the ones actually present in the scoped result set. Union is
        // important because inventory rows carry legacy warehouse names
        // ("Main", "PYT-WH") that aren't in the master master list.
        const subset = scoped("warehouse_name");
        const inRows = collect(subset, (r) => r.warehouse_name);
        const merged = new Set([...wareOpts, ...inRows]);
        return [...merged]
          .filter((w) => inRows.includes(w))
          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
      })(),
      bin_id: collect(scoped("bin_id"), (r) => r.bin_id),
      tags: collect(scoped("tags"), (r) => r.tags),
      product_variant: collect(scoped("product_variant"), (r) => r.product_variant),
      shipment_ids: collect(scoped("shipment_ids"), (r) => r.shipment_ids),
      // Sep 15 2026 — Product Name + System SKU switched from plain text
      // to cascading multi-select; both are finite per-warehouse sets and
      // benefit from the popover's built-in search + blank toggle.
      system_sku: collect(scoped("system_sku"), (r) => r.system_sku),
      product_name: collect(scoped("product_name"), (r) => r.product_name),
    };
  }, [dropdowns, warehouses, rows, advFilters]);

  // When an account is selected, mirror the choice into the warehouse_name
  // filter chip so the table narrows to what that account can actually ship
  // from. Setting an empty warehouse via the "All accounts" option is a no-op.
  useEffect(() => {
    if (!accountFilter) return;
    const acc = accounts.find((a) => a.account_id === accountFilter);
    if (acc?.warehouse_name) {
      setAdvFilters((prev) => ({ ...prev, warehouse_name: acc.warehouse_name }));
    }
  }, [accountFilter, accounts]);

  const activeAdvCount = Object.keys(advFilters).filter((k) => {
    const v = advFilters[k];
    if (!v) return false;
    if (typeof v === "object") return Object.values(v).some((x) => x !== "" && x !== undefined);
    return true;
  }).length;

  const save = async () => {
    try {
      if (editing.id) {
        // Mar 4 2026 — single-call save. Compute the real diff (only truly
        // changed fields) so we never send unchanged values, and route
        // through the correct endpoint based on what actually changed:
        //   • non-qty diff only        → one PATCH to /inventory/{id}
        //   • qty diff only            → open adjust modal (single POST inside)
        //   • qty + non-qty both       → open adjust modal with the non-qty
        //                                 diff passed via `field_updates` so
        //                                 everything commits in ONE API call.
        const original = rows.find((r) => r.id === editing.id) || {};
        const originalQty = Number(original.qty || 0);
        const nextQty = Number(editing.qty || 0);
        const delta = nextQty - originalQty;
        // Build the non-qty diff by walking every key on `editing` and
        // comparing against `original`. Skip computed/protected fields and
        // qty itself.
        const clean = stripComputedFields(editing);
        const nonQtyDiff = {};
        for (const [k, v] of Object.entries(clean)) {
          if (k === "id" || k === "qty") continue;
          const before = original[k];
          const norm = (x) => Array.isArray(x) ? x.join("|") : (x ?? "");
          if (norm(v) !== norm(before)) nonQtyDiff[k] = v;
        }
        if (delta !== 0) {
          // Hand off to the adjust modal — it will POST once to
          // /inventory/{id}/adjust with the qty delta AND the non-qty
          // field_updates so the whole save is a single round-trip.
          setAdjustPrompt({
            row: original,
            delta,
            fieldUpdates: Object.keys(nonQtyDiff).length ? nonQtyDiff : null,
          });
          return;  // Modal will close and refresh rows on success.
        }
        if (!Object.keys(nonQtyDiff).length) {
          toast.info?.("Nothing to save"); setEditing(null);
          return;
        }
        const { data } = await api.patch(`/inventory/${editing.id}`, nonQtyDiff);
        setRows((rs) => rs.map((r) => (r.id === data.id ? data : r)));
      } else {
        if (!String(editing.system_sku || "").trim() || !String(editing.warehouse_name || "").trim()) {
          toast.error("SKU and Warehouse are required to add stock");
          return;
        }
        const { data } = await api.post("/inventory", stripComputedFields(editing));
        setRows((rs) => [data, ...rs]);
      }
      toast.success("Saved"); setEditing(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async () => {
    if (!window.confirm("Delete this SKU?")) return;
    try {
      await api.delete(`/inventory/${editing.id}`);
      setRows((rs) => rs.filter((r) => r.id !== editing.id));
      setEditing(null); toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData(); form.append("file", f);
    try {
      const { data } = await api.post("/inventory/import/csv", form, { headers: { "Content-Type": "multipart/form-data" } });
      // Feb 28 2026 — surface the new DELETE-row counters from the backend
      // (created / updated / deleted / skipped_not_found).
      const parts = [];
      if (data.created)  parts.push(`${data.created} imported`);
      if (data.updated)  parts.push(`${data.updated} updated`);
      if (data.deleted)  parts.push(`${data.deleted} deleted`);
      if (data.skipped_not_found) parts.push(`${data.skipped_not_found} skipped (not found)`);
      toast.success(parts.length ? parts.join(", ") : "Import complete");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };

  // Mar 2 2026 — Inventory Transfer import. Reads a 5-column CSV/XLSX
  // (source_system_sku, source_warehouse, destination_system_sku,
  // destination_warehouse, quantity), validates each row against current
  // inventory, and creates one MOV{N} shipment per valid row (deducting
  // source qty immediately). Invalid rows are surfaced as toasts so ops
  // can fix and re-upload.
  const onTransferImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData(); form.append("file", f);
    try {
      const { data } = await api.post("/transfers/import", form,
        { headers: { "Content-Type": "multipart/form-data" } });
      const created = data.created || 0;
      const errs = data.errors || [];
      if (created > 0) {
        const ids = (data.mov_ids || []).join(", ");
        toast.success(`Created ${created} transfer${created === 1 ? "" : "s"}${ids ? ` — ${ids}` : ""}`);
      }
      if (errs.length) {
        // Surface up to the first 5 errors so ops can see what to fix; the
        // rest are dropped rather than swamping the toast area.
        const preview = errs.slice(0, 5)
          .map((x) => `Row ${x.row}: ${x.reason}`).join(" · ");
        const extra = errs.length > 5 ? ` (+${errs.length - 5} more)` : "";
        toast.error(`${errs.length} row${errs.length === 1 ? "" : "s"} skipped — ${preview}${extra}`, { duration: 10000 });
      }
      if (!created && !errs.length) toast.message("No rows found in the file");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    e.target.value = "";
  };

  // Feb 28 2026 — bulk delete the ticked inventory rows.
  const runBulkDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/inventory/bulk-delete", { ids });
      setSelectedIds(new Set());
      setBulkDeleteConfirm(false);
      toast.success(`${data.deleted} row${data.deleted === 1 ? "" : "s"} deleted.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkDeleting(false); }
  };

  const openHistory = async (row) => {
    try {
      const sku = row.system_sku;
      const [alloc, tl] = await Promise.all([
        api.get(`/inventory/${row.id}/allocations`),
        sku ? api.get(`/procurement/sku/${encodeURIComponent(sku)}/timeline`, { params: row.warehouse_name ? { warehouse_name: row.warehouse_name } : {} }) : Promise.resolve({ data: null }),
      ]);
      setHistoryRecord({ ...alloc.data, timeline: tl.data });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openLogs = async (row) => {
    setLogsRecord({ record: row, logs: [], loading: true });
    try {
      const { data } = await api.get("/logs", {
        params: { module: "inventory", record_id: row.id, limit: 500 },
      });
      // The audit log returns an array (unpaginated) or {items: [...]}.
      const logs = Array.isArray(data) ? data : data.items || [];
      setLogsRecord({ record: row, logs, loading: false });
    } catch (e) {
      toast.error(formatApiError(e));
      setLogsRecord({ record: row, logs: [], loading: false });
    }
  };

  const stockRows = useMemo(
    () => rows.filter((r) => applyFieldFilters(r, advFilters, INVENTORY_FILTER_FIELDS)),
    [rows, advFilters]
  );
  const inTransitAllocatedRows = stockRows.filter((r) => Number(r.in_transit_allocated || 0) > 0);
  const inTransitUnallocatedRows = stockRows.filter((r) => Number(r.in_transit_qty || 0) > 0);
  const placedRows = stockRows.filter((r) => Number(r.qty_placed || 0) > 0);
  const activeRows = tab === "stock" ? stockRows :
    tab === "placed" ? placedRows :
    inTransitTab === "allocated" ? inTransitAllocatedRows : inTransitUnallocatedRows;
  // Click-to-sort on the base grid headers. Falls back to insertion order
  // (server's created_at desc) when no column is selected.
  const sortedActiveRows = useMemo(() => {
    if (!sortKey) return activeRows;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...activeRows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === "number" || typeof bv === "number") {
        return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
      }
      const as = (av ?? "").toString().toLowerCase();
      const bs = (bv ?? "").toString().toLowerCase();
      if (as < bs) return -1 * dir;
      if (as > bs) return 1 * dir;
      return 0;
    });
  }, [activeRows, sortKey, sortDir]);
  const pag = useClientPagination(sortedActiveRows, "inventory");

  return (
    <>
      <PageHeader title="Inventory" subtitle="Stock, in-transit, placed & warehouses" testId={T.moduleTitle("inventory")} module="inventory">
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid={T.searchInput("inventory")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") load(e.currentTarget.value);
            }}
            placeholder="Search SKU, product…"
            className="h-8 pl-7 pr-2 w-56 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        {/* Prominent warehouse + account filters — spec: show only rows a given account can ship from */}
        <select
          data-testid="inventory-warehouse-filter"
          value={typeof advFilters.warehouse_name === "string" ? advFilters.warehouse_name : ""}
          onChange={(e) => { setAdvFilters((f) => ({ ...f, warehouse_name: e.target.value || undefined })); setAccountFilter(""); }}
          className="h-8 px-2 text-[12.5px] border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          title="Filter by warehouse"
        >
          <option value="">All warehouses</option>
          {warehouses.map((w) => <option key={w.name} value={w.name}>{w.name}</option>)}
        </select>
        <select
          data-testid="inventory-account-filter"
          value={accountFilter}
          onChange={(e) => setAccountFilter(e.target.value)}
          className="h-8 px-2 text-[12.5px] border border-slate-200 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
          title="Filter by the warehouse this account is mapped to"
        >
          <option value="">All accounts…</option>
          {accounts.filter((a) => a.warehouse_name).map((a) => (
            <option key={a.account_id} value={a.account_id}>
              {a.account_id} → {a.warehouse_name}
            </option>
          ))}
        </select>
        <Button onClick={() => downloadFile("/inventory/export/csv", "inventory.csv")} testId={T.exportBtn("inventory")}>
          <Download size={13} /> Export
        </Button>
        <label
          className="h-8 px-3 text-[12px] font-medium rounded-sm inline-flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50 cursor-pointer"
          data-testid={T.importBtn("inventory")}
          title={'CSV supports an "action" column: leave blank to upsert, write DELETE to remove a row by System SKU.'}
        >
          <Upload size={13} /> Import
          <input type="file" accept=".csv,.xlsx,.xls" onChange={onImport} className="hidden" />
        </label>
        {/* Mar 2 2026 — Inventory Transfer import. 5-column CSV/XLSX moves
            stock between warehouses via auto-numbered MOV shipments. */}
        <label
          className="h-8 px-3 text-[12px] font-medium rounded-sm inline-flex items-center gap-1.5 border border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 cursor-pointer"
          data-testid="inventory-transfer-import-btn"
          title="CSV/XLSX with columns: source_system_sku, source_warehouse, destination_system_sku, destination_warehouse, quantity. Each row becomes a MOV shipment."
        >
          <Upload size={13} /> Transfer
          <input type="file" accept=".csv,.xlsx,.xls" onChange={onTransferImport} className="hidden" />
        </label>
        <Button onClick={() => setShowFilterBar((s) => !s)} testId="filter-inventory-btn">
          <Filter size={13} /> Filters
          {activeAdvCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9.5px] font-bold text-white bg-blue-600 rounded-full">
              {activeAdvCount}
            </span>
          )}
        </Button>
        <FilterPresets
          moduleKey="inventory"
          value={advFilters}
          onLoad={setAdvFilters}
          extra={{ revealedCols }}
          onLoadExtra={(x) => setRevealedCols(x?.revealedCols || [])}
        />
        {reviewCount > 0 && (
          <span data-testid="inventory-review-badge"
                title="Stock that could not be credited (no warehouse) or duplicate rows without a warehouse. GET /api/inventory/review lists them."
                className="text-[11px] px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-300">
            {reviewCount} inventory item{reviewCount === 1 ? "" : "s"} need review
          </span>
        )}
        <Button variant="primary" onClick={() => setEditing({})} testId={T.newBtn("inventory")}>
          <Plus size={13} /> New
        </Button>
        {deletable && selectedIds.size > 0 && (
          <Button
            variant="danger"
            onClick={() => setBulkDeleteConfirm(true)}
            testId="inventory-bulk-delete-btn"
          >
            <Trash2 size={13} /> Delete {selectedIds.size} Selected
          </Button>
        )}
        {isAdmin && (
          <button
            type="button"
            onClick={wipeAll}
            data-testid="inventory-wipe-all-btn"
            className="h-8 px-3 text-[12px] font-medium rounded-sm inline-flex items-center gap-1.5 border border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
            title="Admin only — permanently delete every inventory row"
          >
            <AlertTriangle size={13} /> Wipe All
          </button>
        )}
      </PageHeader>
      <PageBody>
        {showFilterBar && (
          <div className="mb-3">
            <FieldFilterBar
              moduleKey="inventory"
              fields={INVENTORY_FILTER_FIELDS}
              dropdowns={filterDropdowns}
              value={advFilters}
              onChange={setAdvFilters}
              revealedColumns={revealedCols}
              onToggleColumn={toggleRevealCol}
              rows={rows}
            />
          </div>
        )}

        <div className="border-b border-slate-200 mb-3 flex gap-4">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`inventory-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-1 pb-2 text-[12.5px] border-b-2 -mb-px ${tab === t.key ? "border-slate-900 text-slate-900 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            >
              {t.label}
              <span className="ml-1.5 text-[11px] text-slate-400 font-mono">
                {t.key === "stock" ? stockRows.length : t.key === "placed" ? placedRows.length : (inTransitAllocatedRows.length + inTransitUnallocatedRows.length)}
              </span>
            </button>
          ))}
        </div>

        {tab === "in_transit" && (
          <div className="mb-3 flex gap-2">
            {[["allocated", "Allocated", inTransitAllocatedRows.length], ["unallocated", "Unallocated", inTransitUnallocatedRows.length]].map(([k, l, c]) => (
              <button
                key={k}
                data-testid={`in-transit-tab-${k}`}
                onClick={() => setInTransitTab(k)}
                className={`h-8 px-3 text-[12px] border rounded-sm ${inTransitTab === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
              >
                {l} <span className="ml-1 font-mono opacity-70">({c})</span>
              </button>
            ))}
            <div className="text-[11px] text-slate-500 self-center ml-2 font-mono">
              Allocated in-transit = orders with China Order Status = <b>Dispatched</b>. Removed on <b>Delivered</b>.
            </div>
          </div>
        )}

        <div className="border border-slate-200 bg-white rounded-sm overflow-hidden">
          <div className="max-h-[calc(100vh-260px)] overflow-auto">
            {(() => {
              // Which quantity field the current tab renders as its "primary"
              // Qty column in the base grid. The other 3 quantity fields then
              // fall through to `extraCols` so the user still sees all 24.
              const activeQtyKey = tab === "stock" ? "qty"
                : tab === "placed" ? "qty_placed"
                : inTransitTab === "allocated" ? "in_transit_allocated" : "in_transit_qty";
              // Columns already present in the base grid — a revealed column
              // duplicates itself if we render it again, so we skip these.
              // `image_url` is also rendered as the leading thumbnail column,
              // so we exclude it here to avoid a duplicate URL text column.
              const BASE_COLS = ["system_sku", "product_name", "product_variant",
                "warehouse_name", activeQtyKey, "bin_id", "tags", "image_url"];
              // Per user spec: display ALL 24 fields in the view by default.
              // Every INVENTORY_FILTER_FIELDS entry not already in BASE_COLS is
              // rendered as an extra column — this brings the grid to 24 fields
              // (7 base + 17 extras). Tab-based row filtering still applies.
              const extraCols = INVENTORY_FILTER_FIELDS
                .filter((f) => !BASE_COLS.includes(f.key))
                .filter(Boolean);
              const SortTh = ({ colKey, children, testId }) => (
                <th
                  onClick={() => toggleSort(colKey)}
                  className="cursor-pointer select-none hover:bg-slate-50"
                  title="Click to sort"
                  data-testid={testId}
                >
                  {children}{sortKey === colKey ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
              );
              return (
            <table className="erp-table">
              <thead>
                <tr>
                  {deletable && (
                    <th className="w-8 text-center">
                      <input
                        type="checkbox"
                        checked={pag.pageRows.length > 0 && pag.pageRows.every((r) => selectedIds.has(r.id))}
                        ref={(el) => { if (el) el.indeterminate = pag.pageRows.some((r) => selectedIds.has(r.id)) && !pag.pageRows.every((r) => selectedIds.has(r.id)); }}
                        onChange={() => {
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            const allChecked = pag.pageRows.every((r) => next.has(r.id));
                            if (allChecked) pag.pageRows.forEach((r) => next.delete(r.id));
                            else pag.pageRows.forEach((r) => next.add(r.id));
                            return next;
                          });
                        }}
                        data-testid="inventory-select-all"
                        title="Select all rows on this page"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                  )}
                  <th>Image</th>
                  <SortTh colKey="system_sku">System SKU</SortTh>
                  <SortTh colKey="product_name">Product</SortTh>
                  <SortTh colKey="product_variant">Variant</SortTh>
                  <SortTh colKey="warehouse_name">Warehouse</SortTh>
                  {tab === "stock" && <SortTh colKey={activeQtyKey}>Qty</SortTh>}
                  {tab === "in_transit" && <SortTh colKey={activeQtyKey}>{inTransitTab === "allocated" ? "In-Transit (Alloc.)" : "In-Transit (Unalloc.)"}</SortTh>}
                  {tab === "in_transit" && inTransitTab === "allocated" && <th>Shipment IDs</th>}
                  {tab === "placed" && <SortTh colKey={activeQtyKey}>Qty Placed</SortTh>}
                  {/* Mar 2 2026 — Shipped Qty, sourced from the linked
                      procurement row (system_sku + warehouse_name match). */}
                  <th>Shipped Qty</th>
                  <SortTh colKey="bin_id">Bin</SortTh>
                  <th>Tags</th>
                  {extraCols.map((c) => <SortTh key={c.key} colKey={c.key} testId={`inventory-extra-th-${c.key}`}>{c.label}</SortTh>)}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pag.pageRows.map((r) => (
                  <tr key={r.id} data-testid={T.row("inventory", r.id)} onClick={() => setEditing(stripComputedFields(r))} className={`cursor-pointer ${selectedIds.has(r.id) ? "bg-blue-50" : ""}`}>
                    {deletable && (
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(r.id)}
                          onChange={() => {
                            setSelectedIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
                              return next;
                            });
                          }}
                          data-testid={`inventory-row-check-${r.id}`}
                        />
                      </td>
                    )}
                    <td>{r.image_url ? <ImageThumb src={r.image_url} size={64} testId={`inv-thumb-${r.id}`} /> : "—"}</td>
                    <td className="font-mono">{r.system_sku}</td>
                    <td>{r.product_name}</td>
                    <td>{r.product_variant || "—"}</td>
                    <td>{r.warehouse_name || "—"}</td>
                    {tab === "stock" && <td className="font-mono">{r.qty ?? 0}</td>}
                    {tab === "in_transit" && <td className="font-mono">{inTransitTab === "allocated" ? (r.in_transit_allocated || 0) : (r.in_transit_qty || 0)}</td>}
                    {tab === "in_transit" && inTransitTab === "allocated" && (
                      <td className="font-mono">
                        {(r.shipment_ids || []).length ? (
                          <div className="flex flex-wrap gap-1">
                            {(r.shipment_ids || []).map((sid) => (
                              <span key={sid} className="inline-block h-5 px-1.5 text-[10.5px] bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-sm">{sid}</span>
                            ))}
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                    )}
                    {tab === "placed" && <td className="font-mono">{r.qty_placed || 0}</td>}
                    {(() => {
                      const entry = shippedQtyMap[`${r.system_sku}||${r.warehouse_name}`];
                      if (!entry?.procurement_id) {
                        return <td className="text-slate-400 font-mono">—</td>;
                      }
                      return (
                        <InlineCell
                          testId={`inv-inline-shipped_qty-${r.id}`}
                          type="number"
                          mono
                          value={entry.shipped_qty ?? ""}
                          editable={editable}
                          onSave={(v) => saveShippedQty(r, v)}
                          render={(v) => v === null || v === undefined || v === "" ? <span className="text-slate-400">—</span> : v}
                        />
                      );
                    })()}
                    <td className="font-mono">{r.bin_id || "—"}</td>
                    <td>{r.tags || "—"}</td>
                    {extraCols.map((c) => {
                      const v = r[c.key];
                      let content;
                      if (v === null || v === undefined || v === "") {
                        content = "—";
                      } else if (c.key === "image_url" && typeof v === "string") {
                        content = <ImageThumb src={v} size={64} testId={`inv-extra-thumb-${r.id}`} />;
                      } else if (c.key === "source_url" && typeof v === "string") {
                        content = <a href={v} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-600 hover:underline">{v.length > 32 ? v.slice(0, 32) + "…" : v}</a>;
                      } else if (Array.isArray(v)) {
                        content = v.length ? (
                          <div className="flex flex-wrap gap-1">
                            {v.map((x, i) => (
                              <span key={i} className="inline-block h-5 px-1.5 text-[10.5px] bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-sm">{String(x)}</span>
                            ))}
                          </div>
                        ) : "—";
                      } else if ((c.key === "created_at" || c.key === "updated_at") && typeof v === "string") {
                        content = <span className="font-mono text-[10.5px]">{v.slice(0, 19).replace("T", " ")}</span>;
                      } else {
                        content = String(v);
                      }
                      const isNumeric = c.type === "number-range" || c.type === "money-range";
                      return (
                        <td key={c.key} className={isNumeric ? "font-mono" : ""} data-testid={`inventory-extra-td-${c.key}`}>
                          {content}
                        </td>
                      );
                    })}
                    <td>
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`history-btn-${r.id}`}
                          onClick={(e) => { e.stopPropagation(); openHistory(r); }}
                          className="text-slate-500 hover:text-blue-600"
                          title="Allocation history"
                        >
                          <History size={14} />
                        </button>
                        <button
                          data-testid={`logs-btn-${r.id}`}
                          onClick={(e) => { e.stopPropagation(); openLogs(r); }}
                          className="text-slate-500 hover:text-indigo-600"
                          title="Change log"
                        >
                          <ScrollText size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={deletable ? 10 : 9} className="text-center py-8 text-slate-500">No inventory.</td></tr>}
              </tbody>
            </table>
              );
            })()}
          </div>
        </div>
        <div className="mt-2 flex justify-end">
          <PaginationBar page={pag.page} pageSize={pag.pageSize} total={pag.total}
            onPageChange={pag.setPage} onPageSizeChange={pag.setPageSize} testIdPrefix="inventory" />
        </div>
      </PageBody>

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={() => setEditing(null)}>
          <div className="w-[560px] max-w-full h-full bg-white border-l border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4">
              <div>
                <div className="font-heading text-[14px] font-semibold">{editing.id ? "Edit SKU" : "New SKU"}</div>
                {editing.id && <div className="font-mono text-[11px] text-slate-500">{editing.system_sku}</div>}
              </div>
              <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-auto p-4 grid grid-cols-2 gap-3">
              {[
                // Field order matches the user's Feb-2026 spec exactly.
                { k: "system_sku", label: "System SKU", type: "text" },
                { k: "product_type", label: "Product Type", type: "text" },
                { k: "product_name", label: "Product Name", type: "text", span: 2 },
                { k: "product_variant", label: "Variant", type: "text" },
                { k: "warehouse_name", label: "Warehouse", type: "select", opts: () => warehouses.map((w) => w.name) },
                { k: "qty", label: "Qty in Stock", type: "number" },
                { k: "in_transit_qty", label: "In-Transit (Unallocated)", type: "number" },
                // `qty_placed` + `in_transit_allocated` are auto-derived from
                // orders; they show here for visibility but should stay
                // read-only in the UI (backend also protects them).
                { k: "qty_placed", label: "Qty Placed", type: "number" },
                { k: "in_transit_allocated", label: "In-Transit (Allocated)", type: "number" },
                { k: "bin_id", label: "Bin ID", type: "text" },
                { k: "tags", label: "Tags", type: "text", span: 2 },
                { k: "image_url", label: "Image", type: "image", span: 2 },
                // Physical dimensions (used by order auto-populate)
                { k: "length_cm", label: "Length (cm)", type: "number" },
                { k: "width_cm", label: "Width (cm)", type: "number" },
                { k: "height_cm", label: "Height (cm)", type: "number" },
                { k: "weight_gram", label: "Weight (g)", type: "number" },
                // China sourcing (used by order LIFO seed)
                { k: "source_url", label: "Source URL", type: "text", span: 2 },
                { k: "china_price", label: "China Price ¥", type: "number" },
                { k: "china_shipping", label: "China Shipping ¥", type: "number" },
                { k: "china_weight_gram", label: "China Weight (g)", type: "number" },
                { k: "moq", label: "MOQ", type: "number" },
              ].filter((f) => canView("inventory", f.k)).map((f) => {
                const fieldEditable = editable && canEdit("inventory", f.k);
                const commonProps = {
                  testId: T.formInput(f.k),
                  value: editing[f.k] ?? "",
                };
                return (
                  <Field key={f.k} label={f.label} span={f.span || 1}>
                    {f.type === "select" ? (
                      <Select testId={T.formInput(f.k)} options={f.opts?.() || []} value={editing[f.k]} onChange={(v) => setEditing((s) => ({ ...s, [f.k]: v }))} disabled={!fieldEditable} />
                    ) : f.type === "image" ? (
                      <ImageInput testId={T.formInput(f.k)} value={editing[f.k]} onChange={(v) => setEditing((s) => ({ ...s, [f.k]: v }))} />
                    ) : (
                      <Input
                        {...commonProps}
                        type={f.type === "number" ? "number" : "text"}
                        readOnly={!fieldEditable}
                        className={!fieldEditable ? "readonly-field" : ""}
                        onChange={(e) => setEditing((s) => ({ ...s, [f.k]: f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value }))}
                      />
                    )}
                  </Field>
                );
              })}
              {editing.id && canView("inventory", "in_transit_allocated") && (
                <Field label="In-Transit (Allocated)"><Input value={editing.in_transit_allocated || 0} readOnly className="readonly-field font-mono" /></Field>
              )}
              {editing.id && canView("inventory", "qty_placed") && Number(editing.qty_placed || 0) > 0 && (
                <Field label="Qty Placed (China: Added)"><Input value={editing.qty_placed} readOnly className="readonly-field font-mono" /></Field>
              )}
            </div>
            <div className="h-14 border-t border-slate-200 flex items-center justify-between px-4">
              {editing.id ? (
                <div className="flex gap-2">
                  {editable && <Button variant="danger" onClick={del} testId={T.deleteBtn}><Trash2 size={13} /> Delete</Button>}
                  <Button onClick={() => openHistory(editing)}><History size={13} /> History</Button>
                </div>
              ) : <span />}
              <div className="flex gap-2">
                <Button onClick={() => setEditing(null)} testId={T.cancelBtn}>Cancel</Button>
                {editable && <Button variant="primary" onClick={save} testId={T.saveBtn}>Save</Button>}
              </div>
            </div>
          </div>
        </div>
      )}

      {historyRecord && (
        <div className="fixed inset-0 z-[60] flex justify-end bg-slate-900/30" onClick={() => setHistoryRecord(null)}>
          <div className="w-[860px] max-w-full h-full bg-white border-l border-slate-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4">
              <div>
                <div className="font-heading text-[14px] font-semibold">SKU History & Timeline</div>
                <div className="font-mono text-[11px] text-slate-500">{historyRecord.inventory?.system_sku}</div>
              </div>
              <button onClick={() => setHistoryRecord(null)} className="text-slate-500 hover:text-slate-900"><X size={16} /></button>
            </div>
            {historyRecord.timeline?.summary && (
              <div className="grid grid-cols-4 gap-2 p-4 bg-slate-50 border-b border-slate-200">
                <div className="bg-white border border-slate-200 rounded-sm p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Events</div>
                  <div className="font-mono text-lg text-slate-900 font-bold">{historyRecord.timeline.summary.events}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Avg Transit</div>
                  <div className="font-mono text-lg text-blue-800 font-bold">{historyRecord.timeline.summary.avg_transit_days ?? "—"}<span className="text-[10px] font-normal text-slate-500 ml-1">days</span></div>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Avg Placed→Recv</div>
                  <div className="font-mono text-lg text-indigo-800 font-bold">{historyRecord.timeline.summary.avg_total_days ?? "—"}<span className="text-[10px] font-normal text-slate-500 ml-1">days</span></div>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm p-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Last Transit</div>
                  <div className="font-mono text-lg text-emerald-800 font-bold">{historyRecord.timeline.summary.last_transit_days ?? "—"}<span className="text-[10px] font-normal text-slate-500 ml-1">days</span></div>
                </div>
              </div>
            )}
            <SkuTimelineTrends events={historyRecord.timeline?.events || []} />
            {historyRecord.condition_stocks && (
              <div className="grid grid-cols-3 gap-2 px-4 pb-3">
                {["Correct", "Damaged", "Different"].map((label) => {
                  const q = historyRecord.condition_stocks[label] || 0;
                  const tone = label === "Correct" ? "emerald" : label === "Damaged" ? "red" : "amber";
                  return (
                    <div key={label}
                         className={`border border-${tone}-200 bg-${tone}-50 rounded-sm p-2`}
                         data-testid={`condition-stock-${label.toLowerCase()}`}>
                      <div className={`text-[10px] uppercase tracking-wider text-${tone}-700 font-semibold`}>{label} stock</div>
                      <div className={`font-mono text-lg text-${tone}-800 font-bold`}>{q}</div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex-1 overflow-auto p-4 space-y-6">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">LIFO Procurement Timeline</div>
                <table className="erp-table" data-testid="sku-timeline-table">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>System #</th>
                      <th>Status</th>
                      <th>Qty</th>
                      <th>China ¥</th>
                      <th>Shipping ¥</th>
                      <th>Wt (g)</th>
                      <th>Order Date</th>
                      <th>Dispatched</th>
                      <th>Received</th>
                      <th>Transit</th>
                      <th>Shipment ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(historyRecord.timeline?.events || []).map((e) => (
                      <tr key={`${e.source}-${e.id}`}>
                        <td>
                          <span className={`inline-flex px-1.5 h-5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${e.source === "order" ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>
                            {e.source === "order" ? "Order" : "Direct"}
                          </span>
                        </td>
                        <td className="font-mono">{e.system_id ? `#${e.system_id}` : "—"}</td>
                        <td><StatusPill value={e.china_order_status} tone={statusTone(e.china_order_status)} /></td>
                        <td className="font-mono">{e.china_qty_to_order ?? "—"}</td>
                        <td className="font-mono">{e.china_price ?? "—"}</td>
                        <td className="font-mono">{e.china_shipping ?? "—"}</td>
                        <td className="font-mono">{e.china_weight_gram ?? "—"}</td>
                        <td className="font-mono">{e.china_order_date || "—"}</td>
                        <td className="font-mono">{e.china_order_dispatched_date || "—"}</td>
                        <td className="font-mono">{e.china_order_received_date || "—"}</td>
                        <td className="font-mono text-blue-800 font-semibold">{e.transit_days !== null && e.transit_days !== undefined ? `${e.transit_days}d` : "—"}</td>
                        <td className="font-mono text-indigo-800">{e.china_shipment_id || "—"}</td>
                      </tr>
                    ))}
                    {!(historyRecord.timeline?.events || []).length && (
                      <tr><td colSpan={12} className="text-center py-6 text-slate-500">No procurement events yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Allocations (all orders touching this SKU)</div>
                <table className="erp-table">
                  <thead>
                    <tr>
                      <th>System ID</th>
                      <th>Order ID</th>
                      <th>Account ID</th>
                      <th>Marketplace</th>
                      <th>Qty</th>
                      <th>Allocated</th>
                      <th>Return</th>
                      <th>Recipient</th>
                      <th className="w-24">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(historyRecord.allocations || []).map((a) => {
                      const allocated = Number(a.inventory_allocated_qty || 0);
                      return (
                      <tr key={a.internal_id || a.system_id}>
                        <td>
                          <a href={`/orders?sys=${a.system_id}`} className="font-mono text-indigo-700 hover:underline" data-testid={`alloc-sys-${a.system_id}`}>
                            #{a.system_id}
                          </a>
                        </td>
                        <td className="font-mono text-[11px]">{a.order_id || "—"}</td>
                        <td className="font-mono">{a.account_id || "—"}</td>
                        <td>{a.marketplace}</td>
                        <td className="font-mono">{a.qty ?? "—"}</td>
                        <td className="font-mono">
                          {allocated > 0
                            ? <span className="inline-block px-1.5 py-0.5 rounded-sm border bg-emerald-50 text-emerald-800 border-emerald-300 text-[10.5px] font-bold">Yes · {allocated}</span>
                            : <span className="text-slate-400">No</span>}
                        </td>
                        <td className="font-mono text-[11px]">
                          {a.return_received_status
                            ? <span className={`inline-block px-1.5 py-0.5 rounded-sm border ${a.product_condition === "Correct" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>{a.return_received_status}{a.product_condition ? ` · ${a.product_condition}` : ""}</span>
                            : "—"}
                        </td>
                        <td>{a.recipient_name || "—"}</td>
                        <td>
                          {allocated > 0 && editable && (
                            <button
                              type="button"
                              onClick={async () => {
                                if (!window.confirm(`Release ${allocated} unit(s) from Order #${a.system_id} back into stock?`)) return;
                                try {
                                  await api.post(`/orders/${a.internal_id}/release-inventory`);
                                  toast.success(`Released ${allocated} units`);
                                  openHistory(historyRecord.inventory);  // reload
                                } catch (e) { toast.error(formatApiError(e)); }
                              }}
                              data-testid={`inventory-alloc-release-${a.system_id}`}
                              className="h-6 px-2 text-[10.5px] font-semibold rounded-sm border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                              title={`Release ${allocated} unit(s) back to inventory`}
                            >
                              Release
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                    })}
                    {!(historyRecord.allocations || []).length && (
                      <tr><td colSpan={9} className="text-center py-6 text-slate-500">No allocation events yet.</td></tr>
                    )}
                  </tbody>
                </table>
                {(historyRecord.movements || []).length > 0 && (
                  <>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 mt-6">Inventory Movements (audit trail)</div>
                    <table className="erp-table">
                      <thead>
                        <tr>
                          <th>Action</th>
                          <th>User</th>
                          <th>When</th>
                          <th>Qty change</th>
                          <th>Remaining Qty</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyRecord.movements.map((m, i) => {
                          const det = m.details || {};
                          // Signed delta per action — allocations & manual
                          // decreases + release-from-user are removals (−);
                          // inbound receipts, returns, manual increases,
                          // and releases-back are additions (+). This is
                          // the source of truth for the "Qty change" column.
                          const action = m.action;
                          let delta = null;
                          if (action === "allocation" && det.allocated_qty != null) {
                            delta = -Math.abs(Number(det.allocated_qty));
                          } else if (action === "release" && det.released_qty != null) {
                            delta = Math.abs(Number(det.released_qty));
                          } else if (action === "inbound_receipt" && det.received_qty != null) {
                            delta = Math.abs(Number(det.received_qty));
                          } else if (action?.startsWith("return_") && det.returned_qty != null) {
                            delta = Math.abs(Number(det.returned_qty));
                          } else if (det.delta != null) {
                            // manual_increase / manual_decrease_* — already signed
                            delta = Number(det.delta);
                          }
                          const tone = delta == null
                            ? "text-slate-500"
                            : delta < 0
                              ? "text-red-700 font-bold"
                              : "text-emerald-700 font-bold";
                          const label = delta == null
                            ? "—"
                            : `${delta > 0 ? "+" : ""}${delta}`;
                          return (
                            <tr key={i}>
                              <td>
                                <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10.5px] font-semibold border ${
                                  action === "allocation" ? "bg-red-50 text-red-700 border-red-200" :
                                  action === "release" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                  action === "inbound_receipt" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                  action === "return_correct" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                  action === "return_damaged" || action === "return_different" ? "bg-red-50 text-red-700 border-red-200" :
                                  action === "manual_increase" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                  action?.startsWith("manual_decrease") ? "bg-red-50 text-red-700 border-red-200" :
                                  "bg-slate-100 text-slate-700 border-slate-200"
                                }`}>{action}</span>
                              </td>
                              <td className="font-mono text-[11px]">{m.user_email || "—"}</td>
                              <td className="font-mono text-[11px]">{fmtIst(m.timestamp)}</td>
                              <td className={`font-mono ${tone}`}>{label}</td>
                              <td className="font-mono font-semibold text-slate-800" data-testid={`movement-remaining-qty-${i}`}>{m.remaining_qty ?? "—"}</td>
                              <td className="text-[10.5px] font-mono">
                                {det.condition ? `condition=${det.condition}` : ""}
                                {det.shipment_id ? ` shipment=${det.shipment_id}` : ""}
                                {det.system_id ? ` sys=#${det.system_id}` : ""}
                                {det.released_from_system_id ? ` released-from=#${det.released_from_system_id}` : ""}
                                {det.note ? ` note="${det.note}"` : ""}
                                {det.trigger ? ` trigger=${det.trigger}` : ""}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {bulkDeleteConfirm && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => !bulkDeleting && setBulkDeleteConfirm(false)}>
          <div className="bg-white rounded-md shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="inventory-bulk-delete-confirm">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                <Trash2 size={16} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-[14px] font-bold text-slate-900">Delete {selectedIds.size} selected rows?</h3>
                <p className="text-[12.5px] text-slate-600 mt-1">This cannot be undone. Each inventory row will be removed permanently and an audit log entry will be written.</p>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <Button onClick={() => setBulkDeleteConfirm(false)} disabled={bulkDeleting} testId="inventory-bulk-delete-cancel">Cancel</Button>
              <Button variant="danger" onClick={runBulkDelete} disabled={bulkDeleting} testId="inventory-bulk-delete-confirm-btn">
                {bulkDeleting ? "Deleting…" : `Delete ${selectedIds.size}`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {logsRecord && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm border-2 border-slate-800 w-[90vw] max-w-5xl max-h-[90vh] overflow-auto" data-testid="inventory-logs-modal">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
              <div>
                <div className="text-[13px] font-semibold">Change Log</div>
                <div className="text-[10.5px] font-mono text-slate-500">
                  {logsRecord.record?.system_sku} · {logsRecord.record?.product_name}
                </div>
              </div>
              <button onClick={() => setLogsRecord(null)} className="text-slate-500 hover:text-slate-900" data-testid="inventory-logs-close">
                <X size={16} />
              </button>
            </div>
            <div className="p-3">
              {logsRecord.loading && (
                <div className="text-center py-8 text-slate-500 text-[12px]">Loading…</div>
              )}
              {!logsRecord.loading && logsRecord.logs.length === 0 && (
                <div className="text-center py-8 text-slate-500 text-[12px]">
                  No change entries yet for this inventory item.
                </div>
              )}
              {!logsRecord.loading && logsRecord.logs.length > 0 && (
                <table className="erp-table">
                  <thead>
                    <tr>
                      <th className="w-32">When</th>
                      <th className="w-40">User</th>
                      <th className="w-24">Action</th>
                      <th>Field-level changes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsRecord.logs.map((l, i) => (
                      <tr key={l.id || i}>
                        <td className="font-mono text-[10.5px]">{(l.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                        <td className="font-mono text-[10.5px]">{l.user_email || l.user_id || "—"}</td>
                        <td>
                          <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[10px] font-semibold uppercase ${
                            l.action === "create" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                            l.action === "update" ? "bg-blue-50 text-blue-700 border border-blue-200" :
                            l.action === "delete" ? "bg-red-50 text-red-700 border border-red-200" :
                            "bg-slate-50 text-slate-700 border border-slate-200"
                          }`}>{l.action}</span>
                        </td>
                        <td className="font-mono text-[10.5px]">
                          {Array.isArray(l.changes) && l.changes.length > 0 ? (
                            <div className="space-y-0.5">
                              {l.changes.map((c, j) => (
                                <div key={j}>
                                  <span className="text-slate-600">{c.field}:</span>{" "}
                                  <span className="text-red-700 line-through">{JSON.stringify(c.before ?? "")}</span>
                                  {" → "}
                                  <span className="text-emerald-700 font-semibold">{JSON.stringify(c.after ?? "")}</span>
                                </div>
                              ))}
                            </div>
                          ) : l.action === "create" && l.after ? (
                            <span className="text-emerald-700">Row created with {Object.keys(l.after).length} fields</span>
                          ) : l.action === "delete" && l.before ? (
                            <span className="text-red-700">Row deleted (had {Object.keys(l.before).length} fields)</span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Manual qty-adjust modal — captures reason/condition/source so every
          stock movement lands in the SKU's allocation history (Feb 21 2026). */}
      {adjustPrompt && (
        <AdjustQtyModal
          row={adjustPrompt.row}
          delta={adjustPrompt.delta}
          fieldUpdates={adjustPrompt.fieldUpdates}
          onClose={() => setAdjustPrompt(null)}
          onSaved={(newRow) => {
            // `newRow` is the full inventory doc from the /adjust response
            // so tags, bin_id and every non-qty edit reconcile in one hop.
            setRows((rs) => rs.map((r) => (r.id === adjustPrompt.row.id ? { ...r, ...newRow } : r)));
            setEditing(null);
            setAdjustPrompt(null);
            toast.success("Inventory adjusted");
          }}
        />
      )}
    </>
  );
}

function AdjustQtyModal({ row, delta, fieldUpdates, onClose, onSaved }) {
  const isIncrease = delta > 0;
  const [shipmentId, setShipmentId] = useState("");
  const [condition, setCondition] = useState("correct");
  const [systemId, setSystemId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const payload = { delta };
    if (isIncrease) {
      if (!shipmentId.trim()) { toast.error("Shipment ID is required"); return; }
      payload.shipment_id = shipmentId.trim();
    } else {
      payload.condition = condition;
      if (condition === "correct") {
        if (!systemId.trim()) { toast.error("System ID is required for a Correct-condition deduction"); return; }
        payload.system_id = Number(systemId);
        if (Number.isNaN(payload.system_id)) { toast.error("System ID must be a number"); return; }
      }
    }
    if (note.trim()) payload.note = note.trim();
    // Mar 4 2026 — piggy-back the non-qty diff from the Edit SKU panel
    // so the whole save (qty + tags + bin_id + …) commits in ONE POST.
    if (fieldUpdates && Object.keys(fieldUpdates).length) {
      payload.field_updates = fieldUpdates;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/inventory/${row.id}/adjust`, payload);
      // Response now returns the full inventory doc — hand it up so the
      // parent reconciles the whole row (not just qty).
      onSaved(data.inventory || { qty: data.qty });
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="inventory-adjust-modal">
      <div className="bg-white border border-slate-200 rounded-sm shadow-xl w-full max-w-md">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-heading text-[14px] font-semibold">
              {isIncrease ? "Add stock" : "Remove stock"}
            </div>
            <div className="text-[10.5px] text-slate-500 font-mono mt-0.5">
              {row.system_sku} · {row.warehouse_name || "—"} · {row.qty || 0} → {(row.qty || 0) + delta} (Δ {delta > 0 ? "+" : ""}{delta})
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="inventory-adjust-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {isIncrease ? (
            <div>
              <label className="text-[11px] font-semibold text-slate-700 block mb-1">
                Shipment ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={shipmentId}
                onChange={(e) => setShipmentId(e.target.value)}
                placeholder="e.g. FBA15D9K8"
                data-testid="inventory-adjust-shipment-id"
                className="w-full h-8 px-2 border border-slate-300 rounded-sm text-[12px] font-mono focus:outline-none focus:border-blue-500"
                autoFocus
              />
              <div className="text-[10.5px] text-slate-500 mt-1">
                Where is this stock coming from? Enter the inbound shipment ID so the source is traceable.
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="text-[11px] font-semibold text-slate-700 block mb-1">
                  Stock condition <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  {["correct", "damaged", "different"].map((opt) => (
                    <label
                      key={opt}
                      className={`flex-1 h-8 border rounded-sm inline-flex items-center justify-center text-[11.5px] font-medium cursor-pointer ${
                        condition === opt
                          ? opt === "correct" ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                            : opt === "damaged" ? "border-red-400 bg-red-50 text-red-800"
                            : "border-amber-400 bg-amber-50 text-amber-800"
                          : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <input
                        type="radio"
                        className="sr-only"
                        name="adjust-condition"
                        value={opt}
                        checked={condition === opt}
                        onChange={() => setCondition(opt)}
                        data-testid={`inventory-adjust-condition-${opt}`}
                      />
                      {opt[0].toUpperCase() + opt.slice(1)}
                    </label>
                  ))}
                </div>
              </div>
              {condition === "correct" && (
                <div>
                  <label className="text-[11px] font-semibold text-slate-700 block mb-1">
                    Order System ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={systemId}
                    onChange={(e) => setSystemId(e.target.value)}
                    placeholder="e.g. 4213"
                    data-testid="inventory-adjust-system-id"
                    className="w-full h-8 px-2 border border-slate-300 rounded-sm text-[12px] font-mono focus:outline-none focus:border-blue-500"
                    autoFocus
                  />
                  <div className="text-[10.5px] text-slate-500 mt-1">
                    Which order is this deduction being applied against?
                  </div>
                </div>
              )}
            </>
          )}
          <div>
            <label className="text-[11px] font-semibold text-slate-700 block mb-1">Note (optional)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. box crushed in transit"
              data-testid="inventory-adjust-note"
              className="w-full h-8 px-2 border border-slate-300 rounded-sm text-[12px] focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-8 px-3 text-[12px] font-medium rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            data-testid="inventory-adjust-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className={`h-8 px-4 text-[12px] font-semibold rounded-sm text-white ${
              busy ? "bg-slate-400" : isIncrease ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"
            }`}
            data-testid="inventory-adjust-confirm"
          >
            {busy ? "Saving…" : isIncrease ? "Confirm add" : "Confirm removal"}
          </button>
        </div>
      </div>
    </div>
  );
}
